import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import axios from "axios";
import { Avatar, ThemeProvider } from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { MATERIAL_ADMIN_BASE } from "../../utils/moduleAccess";
import { materialTrackerTheme } from "../../utils/materialTrackerTheme";
import { viewImage } from "../../utils/viewPDF";

const NAV = [
  { group: "Organization" },
  { to: MATERIAL_ADMIN_BASE, end: true, label: "Users", icon: <PeopleAltOutlinedIcon fontSize="small" /> },
  { to: `${MATERIAL_ADMIN_BASE}/create-user`, label: "Add User", icon: <PersonAddAltOutlinedIcon fontSize="small" /> },
  // { to: `${MATERIAL_ADMIN_BASE}/org-details`, label: "Org Details", icon: <BusinessOutlinedIcon fontSize="small" /> },
  { group: "Master Data" },
  { to: `${MATERIAL_ADMIN_BASE}/part-master`, label: "Part Master", icon: <Inventory2OutlinedIcon fontSize="small" /> },
];

// Material Tracker admin area. Uses the same sidebar as the tracker's users (ProcureTrackLayout,
// .pt-app styles) so the app looks the same for every role.
const MaterialAdminLayout = () => {
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");
  const [org, setOrg] = useState();
  const [logoUrl, setLogoUrl] = useState("");

  // Same organization lookup and subscription check as the admin navbar
  useEffect(() => {
    if (!storedUser?.username) return;

    const getOrg = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/organization/get-by-admin?username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        if (res.data.subscriptionEndDate < new Date().toISOString().split("T")[0]) {
          alert("Your organization's subscription has ended. Please contact the admin.");
          localStorage.clear();
          navigate("/");
          return;
        }
        setOrg(res.data);
        if (res.data.orgLogo) {
          setLogoUrl(await viewImage(res.data.orgLogo, storedUser));
        }
      } catch (error) {
        console.error("Error fetching organization:", error);
      }
    };

    getOrg();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const name = storedUser?.username || "Admin";

  return (
    <ThemeProvider theme={materialTrackerTheme}>
      <div className="pt-app pt-admin">
        <nav className="sb">
          <div className="sb-logo">
            <Avatar
              src={logoUrl || undefined}
              alt={org?.orgShortform || name}
              sx={{ bgcolor: logoUrl ? "#fff" : "#4338ca", borderRadius: "3px", fontWeight: 600 }}
              variant="rounded"
              slotProps={{ img: { style: { objectFit: "contain", padding: 3 } } }}
            >
              {name.slice(0, 2).toUpperCase()}
            </Avatar>

            <div>
              <div className="sb-title">{name}</div>
              <div className="sb-sub">Material Tracker admin</div>
            </div>
          </div>

          <div className="sb-nav">
            {NAV.map((n, idx) =>
              n.group ? (
                <div className="sb-group" key={`g-${idx}`}>
                  {n.group}
                </div>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) => "sb-item" + (isActive ? " on" : "")}
                >
                  {n.icon}
                  <span>{n.label}</span>
                </NavLink>
              ),
            )}

            <button type="button" className="sb-item" onClick={handleLogout}>
              <LogoutOutlinedIcon fontSize="small" />
              <span>Logout</span>
            </button>
          </div>
        </nav>

        <main className="main">
          <div className="page">
            <Outlet />
          </div>
        </main>
      </div>
    </ThemeProvider>
  );
};

export default MaterialAdminLayout;
